<script setup lang="ts">
import { ref, onMounted, computed } from "vue";
import { useTranslation } from "i18next-vue";

import { CfgGroups, SettingTypes } from "@/types/enums";
import type { ChangeCallbackParams } from "@/types/config";
import { configStore } from "@/stores/config";
import { addErrorBottom } from "@/stores/messages";

import UCToggle from "@/components/ui/UCToggle.vue";
import UCRange from '@/components/ui/UCRange.vue';

const { t } = useTranslation();

const soundEffects = ref(false);
const hapticFeedback = ref(false);
const soundEffectsVolume = ref<null | number>(0);
const config = configStore();
const settingsSection = ref();

config.$subscribe((mutation, state) => {
  setConfigValues();
});

const soundEffectsVolumeSetting = computed(() => {
  return {
    name: "volume",
    value: soundEffectsVolume.value,
    label: t('settings.sound_haptic.sound_effects_volume'),
    type: SettingTypes.PERCENT,
    group: CfgGroups.sound,
    settings: {
      min: 0,
      max: 100,
      showValue: true,
      valueFormatter: null,
    }
  }
});

function setConfigValues() {
  soundEffects.value = config.$state.config?.sound?.enabled || false;
  hapticFeedback.value = config.$state.config?.haptic?.enabled || false;
  soundEffectsVolume.value = config.$state.config?.sound?.volume || 0;
}

async function soundEffectsVolumeChanged(params: ChangeCallbackParams) {
  soundEffectsVolume.value = params.value as number;
  onItemChange(params);
}

async function soundEffectsChanged() {
  const params: ChangeCallbackParams = {
    group: CfgGroups.sound,
    name: "enabled",
    value: soundEffects.value
  }
  
  onItemChange(params);
}

async function hapticFeedbackChanged() {
  const params: ChangeCallbackParams = {
    group: CfgGroups.haptic,
    name: "enabled",
    value: soundEffects.value
  }

  onItemChange(params);
}


async function onItemChange(params: ChangeCallbackParams) {
  try {
    await config.update(params.group as string, params.name as string, params.value);
  } catch (e: any) {
    addErrorBottom(e, null, settingsSection.value);
  }
}

onMounted(() => {
  setConfigValues();
})

</script>
<template>
  <div class="page-settings-section page-settings-section--sound-haptic" ref="settingsSection">
    <h1 class="page-settings-section__title">
      {{ $t('settings.sound_haptic.name') }}
    </h1>
    <div class="page-settings-section__main">
      <UCToggle
        v-model="soundEffects"
        @change="soundEffectsChanged"
        :label="$t('settings.sound_haptic.sound_effects')"
        :full-w="true"
        :settings="true"
        :description="$t('settings.sound_haptic.sound_effects_description')"
      />
      <UCRange
        v-model="soundEffectsVolume"
        name="volume"
        :key="'sound-effects-volume'"
        :definition="soundEffectsVolumeSetting"
        :change-callback="soundEffectsVolumeChanged"
        :value="soundEffectsVolume!"
        :settings="true"
        />
      <UCToggle
        v-model="hapticFeedback"
        @change="hapticFeedbackChanged"
        :label="$t('settings.sound_haptic.haptic_feedback')"
        :full-w="true"
        :settings="true"
        :description="$t('settings.sound_haptic.haptic_feedback_description')"
      />
    </div>
  </div>
</template>